import React, { useState, useCallback } from 'react';
import { Button } from './Button';
import { useToast } from '../../hooks/useToast';
import { usePatchJobMutation } from '../../store/slices/apiSlice';
import type { JobDatesData } from '../../utils/types';
import type { JobDatesManagerProps } from '../../types/components';
import { Calendar, Clock, Package, Lock, Info } from 'lucide-react';
import { useJobLock } from '../../hooks/useJobLock';
import { formatDateForInput, validateJobDateField } from '../../utils/jobDateValidation';

// Debounce utility function
const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// Interface is now centralized in types/components.ts

export const JobDatesManager: React.FC<JobDatesManagerProps> = ({
  jobId,
  currentDates,
  fulfillmentType: _fulfillmentType,
  assignedDeliveryUserRole,
  className = '',
  onDatesUpdate,
  lockData,
  datesReadOnly = false,
  datesReadOnlyHint,
  hideGlassArrival = false,
}) => {
  const toast = useToast();
  const [patchJob, { isLoading }] = usePatchJobMutation();
  
  // Lock checking
  const { canModifyDates, showLockError } = useJobLock({
    jobId,
    currentLock: lockData || null,
  });
  
  // Debug logging
  React.useEffect(() => {
    console.log('🔍 JobDatesManager Debug:', {
      jobId,
      lockData,
      canModifyDates,
      isLocked: lockData?.is_locked
    });
  }, [jobId, lockData, canModifyDates]);
  
  // Local state for date inputs
  const [dates, setDates] = useState({
    glass_arrival_date: currentDates.glass_arrival_date || '',
    manufacture_date: currentDates.manufacture_date || '',
    potential_delivery_date: currentDates.potential_delivery_date || ''
  });

  const datesRef = React.useRef(dates);
  React.useEffect(() => {
    datesRef.current = dates;
  }, [dates]);

  const setSameAsGlassArrival = () => {
    if (datesReadOnly) {
      return;
    }
    if (!dates.glass_arrival_date) {
      toast.error('Glass Arrival Required', 'Please set Glass Arrival date first');
      return;
    }
    
    const previousValue = dates.manufacture_date;
    setDates(prev => ({ ...prev, manufacture_date: dates.glass_arrival_date }));
    debouncedSave('manufacture_date', dates.glass_arrival_date, previousValue);
    toast.info('Date Copied', 'Manufacturing date set to same as Glass Arrival');
  };

  const handleDateUpdate = useCallback(async (field: keyof JobDatesData, value: string, revertValue?: string) => {
    const validationError = validateJobDateField(field, value, {
      allDates: datesRef.current,
      hideGlassArrival,
      assignedDeliveryUserRole,
    });
    if (validationError) {
      // Clear invalid date to make it easier for user to enter new valid date
      setDates(prev => ({ ...prev, [field]: null }));
      toast.error('Date Validation Error', validationError);
      return;
    }
    
    try {
      const updateData: Partial<JobDatesData> = {
        [field]: value || null
      };
      
      await patchJob({ 
        id: jobId, 
        data: updateData as any
      }).unwrap();
      
      // Update local state
      setDates(prev => ({ ...prev, [field]: value }));
      
      // Notify parent component
      onDatesUpdate?.(updateData);
      
      const fieldLabels = {
        glass_arrival_date: 'Glass Arrival Date',
        manufacture_date: 'Manufacturing Date', 
        potential_delivery_date: 'Potential Delivery Date'
      };
      
      toast.success(
        'Date Updated', 
        `${fieldLabels[field]} ${value ? 'updated' : 'cleared'} successfully`
      );
      
    } catch (error: any) {
      // Revert to previous value if API call fails
      if (revertValue !== undefined) {
        setDates(prev => ({ ...prev, [field]: revertValue }));
      }
      toast.error('Update Failed', error?.data?.detail || 'Please try again');
    }
  }, [jobId, patchJob, toast, onDatesUpdate, hideGlassArrival, assignedDeliveryUserRole]);

  // Debounced auto-save function
  const debouncedSave = useCallback(
    debounce((field: keyof JobDatesData, value: string, revertValue?: string) => {
      handleDateUpdate(field, value, revertValue);
    }, 400),
    [handleDateUpdate]
  );

  // Auto-save handler for date inputs
  const handleDateChange = (field: keyof JobDatesData, value: string) => {
    if (datesReadOnly) {
      return;
    }
    // Check lock ONLY for potential_delivery_date
    if (field === 'potential_delivery_date' && !canModifyDates) {
      console.log('🔒 BLOCKED: Potential delivery date is locked');
      showLockError();
      return;
    }
    
    console.log(`✅ ALLOWED: Changing ${field} (lock only applies to potential_delivery_date)`);
    const previousValue = dates[field];
    
    // Update local state immediately for responsive UI
    setDates(prev => ({ ...prev, [field]: value }));
    
    // Auto-save with debounce
    debouncedSave(field, value, previousValue);
  };

  const handleClearDate = (field: keyof JobDatesData) => {
    if (datesReadOnly) {
      return;
    }
    // Check lock ONLY for potential_delivery_date
    if (field === 'potential_delivery_date' && !canModifyDates) {
      showLockError();
      return;
    }
    
    const previousValue = dates[field];
    setDates(prev => ({ ...prev, [field]: '' }));
    debouncedSave(field, '', previousValue);
  };

  React.useEffect(() => {
    setDates({
      glass_arrival_date: currentDates.glass_arrival_date || '',
      manufacture_date: currentDates.manufacture_date || '',
      potential_delivery_date: currentDates.potential_delivery_date || ''
    });
  }, [currentDates]);

  const inputsLocked = isLoading || datesReadOnly;

  return (
    <div className={`space-y-3 ${className}`}>
      <h4
        className={`text-sm font-medium ${
          datesReadOnly ? 'text-gray-500' : 'text-gray-700'
        }`}
      >
        Production Dates
      </h4>

      {datesReadOnly && datesReadOnlyHint && (
        <div className="bg-slate-100 border border-slate-200 rounded-md p-2 flex items-start gap-2">
          <Info className="w-4 h-4 text-slate-600 flex-shrink-0 mt-0.5" />
          <span className="text-xs text-slate-800">{datesReadOnlyHint}</span>
        </div>
      )}
      
      {/* Lock warning banner */}
      {!datesReadOnly && !canModifyDates && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-2 flex items-center gap-2">
          <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <span className="text-xs text-amber-800">
            Potential Delivery Date is locked and cannot be modified
          </span>
        </div>
      )}
      
      <div
        className={`rounded-lg p-4 space-y-6 ${
          datesReadOnly
            ? 'bg-gray-100 border border-gray-200 opacity-95'
            : 'bg-green-50'
        }`}
      >
        {/* Glass Arrival Date */}
        {!hideGlassArrival && (
          <div>
            <div className="flex items-center mb-3">
              <Package
                className={`w-4 h-4 mr-2 ${datesReadOnly ? 'text-gray-400' : 'text-green-600'}`}
              />
              <span
                className={`text-sm font-medium uppercase tracking-wide ${
                  datesReadOnly ? 'text-gray-500' : 'text-gray-700'
                }`}
              >
                Glass Arrival
              </span>
            </div>
            <div className="flex gap-2 items-center">
              <input
                type="date"
                value={formatDateForInput(dates.glass_arrival_date)}
                onChange={(e) => handleDateChange('glass_arrival_date', e.target.value)}
                disabled={inputsLocked}
                className="form-input text-sm flex-1"
              />
              {currentDates.glass_arrival_date && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleClearDate('glass_arrival_date')}
                  disabled={inputsLocked}
                  className="text-xs text-red-600 hover:text-red-700 px-2"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Manufacturing Date */}
        <div>
          <div
            className={`flex items-center mb-3 ${hideGlassArrival ? '' : 'justify-between'}`}
          >
            <div className="flex items-center">
              <Calendar
                className={`w-4 h-4 mr-2 ${datesReadOnly ? 'text-gray-400' : 'text-blue-600'}`}
              />
              <span
                className={`text-sm font-medium uppercase tracking-wide ${
                  datesReadOnly ? 'text-gray-500' : 'text-gray-700'
                }`}
              >
                Manufacturing
              </span>
            </div>
            {!hideGlassArrival && (
              <Button
                size="sm"
                variant="secondary"
                onClick={setSameAsGlassArrival}
                disabled={inputsLocked || !dates.glass_arrival_date}
                className="text-xs"
              >
                Same Date
              </Button>
            )}
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="date"
              value={formatDateForInput(dates.manufacture_date)}
              onChange={(e) => handleDateChange('manufacture_date', e.target.value)}
              min={hideGlassArrival ? undefined : dates.glass_arrival_date || undefined}
              disabled={inputsLocked}
              className="form-input text-sm flex-1"
            />
            {currentDates.manufacture_date && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleClearDate('manufacture_date')}
                disabled={inputsLocked}
                className="text-xs text-red-600 hover:text-red-700 px-2"
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Potential Delivery Date */}
        <div>
          <div className="flex items-center mb-3">
            <Clock
              className={`w-4 h-4 mr-2 ${datesReadOnly ? 'text-gray-400' : 'text-purple-600'}`}
            />
            <span
              className={`text-sm font-medium uppercase tracking-wide ${
                datesReadOnly ? 'text-gray-500' : 'text-gray-700'
              }`}
            >
              Potential Delivery
            </span>
          </div>
          <div className="flex gap-2 items-center">
            <input
              type="datetime-local"
              value={formatDateForInput(dates.potential_delivery_date)}
              onChange={(e) => handleDateChange('potential_delivery_date', e.target.value)}
              min={
                hideGlassArrival
                  ? dates.manufacture_date || undefined
                  : dates.manufacture_date || dates.glass_arrival_date || undefined
              }
              disabled={inputsLocked || !canModifyDates}
              className="form-input text-sm flex-1"
            />
            {currentDates.potential_delivery_date && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleClearDate('potential_delivery_date')}
                disabled={inputsLocked || !canModifyDates}
                className="text-xs text-red-600 hover:text-red-700 px-2"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
